import { IApplyInfoComment, IFiltersList } from "@/src/types/applicationOnline";
import { formatDateToDisplay } from "@/src/utils/formatDateToApiIso";
import { resolveOfficeAccountingGeneralStatusLabel } from "@/src/constants/officeAccountingTableConfig";
import { resolveInvitationFeeFromFormOfEducation } from "@/src/utils/officeAccountingColumnUpdate";
import { resolveDisplayDegreeAndCourse } from "@/src/utils/admissionOffice/resolveDisplayDegreeAndCourse";
import { stripCalcCommentMeta } from "@/src/utils/calculateCommentMeta";
import {
  IApplicantAccountingListItem,
  IApplicantInfoDetail,
  IApplicantInfoPayload,
  IApplicantUserInfoApi,
  IOfficeAccountingIncomeRecord,
  IOfficeAccountingTableRow,
} from "@/src/types/officeAccounting";
import { formatOfficeAccountingUsdAmount, parseOfficeAccountingAmount } from "@/src/utils/officeAccountingTotalAmountUsd";

export const normalizeApplicantComments = (source: unknown): IApplyInfoComment[] => {
  if (!Array.isArray(source)) return [];

  return source
    .map((item) => {
      if (item == null || typeof item !== "object") return null;
      const comment = item as Record<string, unknown>;
      const id = typeof comment.id === "number" ? comment.id : Number(comment.id);
      if (!Number.isFinite(id)) return null;

      return {
        id,
        text: comment.text == null ? "" : String(comment.text),
        created_at: comment.created_at == null ? "" : String(comment.created_at),
        commentator: comment.commentator == null ? "" : String(comment.commentator),
        commentator_role: comment.commentator_role == null ? "" : String(comment.commentator_role),
        commentator_photo: comment.commentator_photo == null ? null : String(comment.commentator_photo),
      };
    })
    .filter((comment): comment is IApplyInfoComment => comment != null);
};

export const sortCommentsNewestFirst = (comments: IApplyInfoComment[]): IApplyInfoComment[] =>
  [...comments].sort((a, b) => {
    const timeA = Date.parse(a.created_at);
    const timeB = Date.parse(b.created_at);
    if (Number.isFinite(timeA) && Number.isFinite(timeB)) return timeB - timeA;
    return b.id - a.id;
  });

const pickIncomeString = (value: unknown): string => (value == null ? "" : String(value).trim());

export const normalizeOfficeAccountingIncomeRecords = (source: unknown): IOfficeAccountingIncomeRecord[] => {
  if (!Array.isArray(source)) return [];

  const records: IOfficeAccountingIncomeRecord[] = [];
  source.forEach((item) => {
    if (item == null || typeof item !== "object") return;
    const record = item as Record<string, unknown>;
    const idRaw = record.id;
    const id = typeof idRaw === "number" ? idRaw : Number(idRaw);
    records.push({
      ...(Number.isFinite(id) ? { id } : {}),
      date: pickIncomeString(record.date) || null,
      invoice_number: pickIncomeString(record.invoice_number) || null,
      count: pickIncomeString(record.count) || null,
      currency: pickIncomeString(record.currency) || null,
      coefficient: pickIncomeString(record.coefficient) || null,
      account: pickIncomeString(record.account) || null,
      result: pickIncomeString(record.result) || null,
      note: pickIncomeString(record.note) || null,
    });
  });
  return records;
};

export const sumOfficeAccountingIncomeRecords = (records: IOfficeAccountingIncomeRecord[]): number =>
  records.reduce((sum, record) => sum + parseOfficeAccountingAmount(record.result), 0);

export const formatOfficeAccountingIncomeRecordsTotal = (records: IOfficeAccountingIncomeRecord[]): string => {
  if (!records.length) return "";
  return formatOfficeAccountingUsdAmount(sumOfficeAccountingIncomeRecords(records));
};

const formatIncomeRecordDateDisplay = (raw: string): string => formatDateToDisplay(raw) || raw.trim();

const toDisplayDate = (value: unknown): string => {
  const raw = typeof value === "string" ? value : value == null ? "" : String(value);
  return formatDateToDisplay(raw) || raw.trim();
};

/** `date` from the first object in `*_records` (newest / last added), as DD.MM.YYYY. */
export const getLastOfficeAccountingIncomeRecordDate = (records: IOfficeAccountingIncomeRecord[]): string => {
  if (!records.length) return "";
  const raw = pickIncomeString(records[0]?.date);
  return raw ? formatIncomeRecordDateDisplay(raw) : "";
};

export const getLatestCommentText = (comments: IApplyInfoComment[]): string => {
  const latest = sortCommentsNewestFirst(comments)[0]?.text ?? "";
  return stripCalcCommentMeta(latest);
};

const toDisplayString = (value: unknown): string => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    return value
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          const obj = item as Record<string, unknown>;
          return String(obj.text ?? obj.value ?? obj.title ?? obj.message ?? "");
        }
        return String(item);
      })
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") return "";
  return String(value);
};

const parseStudentId = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number.parseInt(value.replace(/\D+/g, ""), 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

/** Accepts new (`user_info`/`applicant_info`) and legacy (`userInfo`/`applicantInfo`) shapes. */
type GroupedApplicantRow = {
  student_id?: number | string;
  user_info?: IApplicantUserInfoApi;
  applicant_info?: IApplicantInfoDetail;
  userInfo?: IApplicantUserInfoApi & { fullName?: string; campus?: string; student_id?: number | string; id?: number | string };
  applicantInfo?: IApplicantInfoDetail;
};

const isGroupedRow = (item: unknown): item is GroupedApplicantRow =>
  typeof item === "object" &&
  item !== null &&
  ("applicant_info" in item || "user_info" in item || "applicantInfo" in item || "userInfo" in item);

const pickUserInfo = (row: GroupedApplicantRow): IApplicantUserInfoApi | undefined => row.user_info ?? row.userInfo;
const pickApplicantInfo = (row: GroupedApplicantRow): IApplicantInfoDetail | undefined => row.applicant_info ?? row.applicantInfo;

const mapApplicantFields = (source: IApplicantInfoPayload | undefined): IApplicantInfoPayload => {
  if (!source) return {};
  return { ...source };
};

const pickString = (...values: unknown[]): string => {
  for (const value of values) {
    const normalized = toDisplayString(value);
    if (normalized) return normalized;
  }
  return "";
};

export const mapApplicantDetailToTableRow = (
  studentInfoId: number,
  userInfo: (IApplicantUserInfoApi & { fullName?: string; campus?: string }) | undefined,
  applicantInfo: IApplicantInfoDetail | undefined,
  fallback?: Record<string, unknown>,
): IOfficeAccountingTableRow => {
  const applicant = mapApplicantFields(applicantInfo);
  const flat = (fallback ?? {}) as Record<string, unknown>;
  const admissionNo = toDisplayString(applicant.admissionNo ?? flat.admissionNo);
  const commentsSource = userInfo?.comments ?? applicantInfo?.comments ?? flat.comments;
  const commentsList = normalizeApplicantComments(commentsSource);

  const applicantId = parseStudentId(flat.applicant_id ?? flat.applicantId ?? flat.student_id);
  const taskId = parseStudentId(flat.task_id ?? flat.taskId);
  const rawCourse = pickString(applicant.course, flat.course, userInfo?.course);
  const rawLevelEducation = pickString(
    applicant.levelEducation,
    (applicant as { level_education?: string }).level_education,
    userInfo?.levelEducation,
    flat.levelEducation,
    flat.level_education,
  );
  const { degree, course } = resolveDisplayDegreeAndCourse(rawCourse, rawLevelEducation);

  return {
    student_info_id: studentInfoId,
    applicant_id: applicantId,
    task_id: taskId,
    color_star: toDisplayString(flat.color_star) || "default",
    officeAccountingId: studentInfoId > 0 ? String(studentInfoId) : "",
    applicantId: applicantId > 0 ? String(applicantId) : "",
    fullName: toDisplayString(userInfo?.fullname ?? userInfo?.fullName ?? flat.fullName ?? flat.fullname),
    comments: getLatestCommentText(commentsList),
    commentsList,
    invitationFeePaidRecords: [],
    paymentFactUsdRecords: [],
    campus: toDisplayString(userInfo?.form_office ?? userInfo?.campus ?? flat.form_office ?? flat.campus),
    country: toDisplayString(userInfo?.country ?? flat.country),
    dateOfBirth: toDisplayDate(userInfo?.dateOfBirth ?? flat.dateOfBirth),
    passportNo: toDisplayString(userInfo?.passportNo ?? flat.passportNo),
    passportIssued: toDisplayDate(userInfo?.passportIssued ?? flat.passportIssued),
    validPassport: toDisplayDate(userInfo?.validPassport ?? flat.validPassport),
    phone: toDisplayString(userInfo?.phone ?? flat.phone),
    email: toDisplayString(userInfo?.email ?? flat.email),
    managersEmail: toDisplayString(applicant.managersEmail ?? flat.managersEmail),
    agentsEmail: toDisplayString(applicant.agentsEmail ?? flat.agentsEmail),
    course,
    levelEducation: degree,
    fieldOfStudy: toDisplayString(applicant.fieldOfStudy ?? flat.fieldOfStudy),
    formOfEducation: toDisplayString(applicant.formOfEducation ?? flat.formOfEducation),
    duration: toDisplayString(applicant.duration ?? flat.duration),
    mediumOfInstruction: toDisplayString(applicant.mediumOfInstruction ?? flat.mediumOfInstruction),
    admissionSemester: toDisplayString(applicant.admissionSemester ?? flat.admissionSemester),
    intake: toDisplayString(applicant.intake ?? flat.intake),
    currentSemester: toDisplayString(applicant.currentSemester ?? flat.currentSemester),
    generalStatus: toDisplayString(applicant.generalStatus ?? flat.generalStatus),
    admissionNo,
    dataAdmission: toDisplayDate(applicant.dataAdmission ?? flat.dataAdmission),
    tuitionFee: toDisplayString(applicant.tuitionFee ?? flat.tuitionFee),
    certificationEqualization: toDisplayString(applicant.certificationEqualization ?? flat.certificationEqualization),
    statusAdmissionPayment: toDisplayString(applicant.statusAdmissionPayment ?? flat.statusAdmissionPayment),
    groupNo: toDisplayString(applicant.groupNo ?? flat.groupNo),
    residencePermit: toDisplayString(applicant.residencePermit ?? flat.residencePermit),
    validUntil: toDisplayDate(applicant.validUntil ?? flat.validUntil),
    orderOfThePD: toDisplayString(applicant.orderOfThePD ?? flat.orderOfThePD),
    opDData: toDisplayDate(applicant.opDData ?? flat.opDData),
    expulsionOfThePd: toDisplayString(applicant.expulsionOfThePd ?? flat.expulsionOfThePd),
    epDData: toDisplayDate(applicant.epDData ?? flat.epDData),
    orderOfTheMc: toDisplayString(applicant.orderOfTheMc ?? flat.orderOfTheMc),
    omcData: toDisplayDate(applicant.omcData ?? flat.omcData),
    expulsionOfTheMc: toDisplayString(applicant.expulsionOfTheMc ?? flat.expulsionOfTheMc),
    emcData: toDisplayDate(applicant.emcData ?? flat.emcData),
    orderForAcademicLeave: toDisplayString(applicant.orderForAcademicLeave ?? flat.orderForAcademicLeave),
    durationAl: toDisplayString(applicant.durationAl ?? flat.durationAl),
    statusAl: toDisplayString(applicant.statusAl ?? flat.statusAl),
    emailStudentInBase: toDisplayString(applicant.emailStudentInBase ?? flat.emailStudentInBase),
    statusEmail: toDisplayString(applicant.statusEmail ?? flat.statusEmail),
    dateAddEdbo: toDisplayDate(applicant.dateAddEdbo ?? flat.dateAddEdbo),
    statusEdbo: toDisplayString(applicant.statusEdbo ?? flat.statusEdbo),
    invitationFee:
      toDisplayString(applicant.invitationFee ?? flat.invitationFee) ||
      resolveInvitationFeeFromFormOfEducation(toDisplayString(applicant.formOfEducation ?? flat.formOfEducation)) ||
      "",
    ifDate: toDisplayDate(applicant.ifDate ?? flat.ifDate),
    invitationFeePaid: toDisplayString(applicant.invitationFeePaid ?? flat.invitationFeePaid),
    ifDatePaid: toDisplayDate(applicant.ifDatePaid ?? flat.ifDatePaid),
    statusInvitationFee: toDisplayString(applicant.statusInvitationFee ?? flat.statusInvitationFee),
    invoiceNumber: toDisplayString(applicant.invoiceNumber ?? flat.invoiceNumber),
    dataInvoice: toDisplayDate(applicant.dataInvoice ?? flat.dataInvoice),
    invoiceAmount: toDisplayString(applicant.invoiceAmount ?? flat.invoiceAmount),
    paymentFactUsd: toDisplayString(applicant.paymentFactUsd ?? flat.paymentFactUsd),
    creditDateUsd: toDisplayDate(applicant.creditDateUsd ?? flat.creditDateUsd),
    totalAmountUsd: toDisplayString(applicant.totalAmountUsd ?? flat.totalAmountUsd),
    totalPaidUsd: toDisplayString(applicant.totalPaidUsd ?? flat.totalPaidUsd),
    debtBalanceUsd: toDisplayString(applicant.debtBalanceUsd ?? flat.debtBalanceUsd),
    balanceStatusUsd: toDisplayString(applicant.balanceStatusUsd ?? flat.balanceStatusUsd),
    paymentFactEur: toDisplayString(applicant.paymentFactEur ?? flat.paymentFactEur),
    creditDateEur: toDisplayDate(applicant.creditDateEur ?? flat.creditDateEur),
    totalMissedClassesEur: toDisplayString(applicant.totalMissedClassesEur ?? flat.totalMissedClassesEur),
    balanceMissedClassesEur: toDisplayString(applicant.balanceMissedClassesEur ?? flat.balanceMissedClassesEur),
    statusMissedClasses: toDisplayString(applicant.statusMissedClasses ?? flat.statusMissedClasses),
    totalNostrificationsEur: toDisplayString(applicant.totalNostrificationsEur ?? flat.totalNostrificationsEur),
    balanceNostrificationsEur: toDisplayString(applicant.balanceNostrificationsEur ?? flat.balanceNostrificationsEur),
    statusNostrification: toDisplayString(applicant.statusNostrification ?? flat.statusNostrification),
    preparatoryDepartment: toDisplayString(applicant.preparatoryDepartment ?? flat.preparatoryDepartment),
    costFirstSemester: toDisplayString(applicant.costFirstSemester ?? flat.costFirstSemester),
    costSecondSemester: toDisplayString(applicant.costSecondSemester ?? flat.costSecondSemester),
    costThirdSemester: toDisplayString(applicant.costThirdSemester ?? flat.costThirdSemester),
    costFouthSemester: toDisplayString(applicant.costFouthSemester ?? flat.costFouthSemester),
    costFifthSemester: toDisplayString(applicant.costFifthSemester ?? flat.costFifthSemester),
    costSixthSemester: toDisplayString(applicant.costSixthSemester ?? flat.costSixthSemester),
    costSeventhSemester: toDisplayString(applicant.costSeventhSemester ?? flat.costSeventhSemester),
    costEighthSemester: toDisplayString(applicant.costEighthSemester ?? flat.costEighthSemester),
    costNinthSemester: toDisplayString(applicant.costNinthSemester ?? flat.costNinthSemester),
    costTenthSemester: toDisplayString(applicant.costTenthSemester ?? flat.costTenthSemester),
    costEleventhSemester: toDisplayString(applicant.costEleventhSemester ?? flat.costEleventhSemester),
    costTwelfthSemester: toDisplayString(applicant.costTwelfthSemester ?? flat.costTwelfthSemester),
    pgPhdCostFirstSemester: toDisplayString(applicant.pgPhdCostFirstSemester ?? flat.pgPhdCostFirstSemester),
    pgPhdCostSecondSemester: toDisplayString(applicant.pgPhdCostSecondSemester ?? flat.pgPhdCostSecondSemester),
    pgPhdCostThirdSemester: toDisplayString(applicant.pgPhdCostThirdSemester ?? flat.pgPhdCostThirdSemester),
    pgPhdCostFourthSemester: toDisplayString(applicant.pgPhdCostFourthSemester ?? flat.pgPhdCostFourthSemester),
    pgPhdCostFifthSemester: toDisplayString(applicant.pgPhdCostFifthSemester ?? flat.pgPhdCostFifthSemester),
    pgPhdCostSixthSemester: toDisplayString(applicant.pgPhdCostSixthSemester ?? flat.pgPhdCostSixthSemester),
    otherExpenses: toDisplayString(applicant.otherExpenses ?? flat.otherExpenses),
    transfer: toDisplayString(applicant.transfer ?? flat.transfer),
    insurance: toDisplayString(applicant.insurance ?? flat.insurance),
    admissionFee: toDisplayString(applicant.admissionFee ?? flat.admissionFee),
    temporaryResidencePermission: toDisplayString(applicant.temporaryResidencePermission ?? flat.temporaryResidencePermission),
    hostelAccommodation: toDisplayString(applicant.hostelAccommodation ?? flat.hostelAccommodation),
    paymentForMissedClasses: toDisplayString(applicant.paymentForMissedClasses ?? flat.paymentForMissedClasses),
    nostrification: toDisplayString(applicant.nostrification ?? flat.nostrification),
  };
};

export const mapApplicantAccountingItemToTableRow = (item: IApplicantAccountingListItem): IOfficeAccountingTableRow => {
  const task = item.task_info ?? undefined;
  const applicant = item.applicant_info ?? undefined;
  const studentInfoId = typeof item.id === "number" && Number.isFinite(item.id) ? item.id : 0;
  const applicantId =
    (typeof item.applicant_id === "number" && Number.isFinite(item.applicant_id) ? item.applicant_id : 0) ||
    (typeof applicant?.id === "number" && Number.isFinite(applicant.id) ? applicant.id : 0);
  const taskId =
    (typeof item.task_id === "number" && Number.isFinite(item.task_id) ? item.task_id : 0) ||
    (typeof task?.id === "number" && Number.isFinite(task.id) ? task.id : 0);
  const admissionNo = pickString(item.admission_no);
  const commentsList = normalizeApplicantComments(item.comments);
  const invitationFeePaidRecords = normalizeOfficeAccountingIncomeRecords(item.invitation_fee_paid_records);
  const paymentFactUsdRecords = normalizeOfficeAccountingIncomeRecords(item.payment_fact_usd_records);
  const rawCourse = pickString(applicant?.course, task?.selected_course);
  const rawLevelEducation = pickString(applicant?.level_education);
  const { degree, course } = resolveDisplayDegreeAndCourse(rawCourse, rawLevelEducation);

  return {
    student_info_id: studentInfoId,
    applicant_id: applicantId,
    task_id: taskId,
    color_star: pickString(item.color_star) || "default",
    officeAccountingId: studentInfoId > 0 ? String(studentInfoId) : "",
    applicantId: applicantId > 0 ? String(applicantId) : "",
    fullName: pickString(applicant?.fullname),
    comments: getLatestCommentText(commentsList),
    commentsList,
    invitationFeePaidRecords,
    paymentFactUsdRecords,
    campus: pickString(item.form_office, applicant?.form_office),
    form_office: pickString(item.form_office, applicant?.form_office),
    office_url: pickString(
      (item as { office_url?: string | null }).office_url,
      (applicant as { office_url?: string | null } | undefined)?.office_url,
    ),
    country: pickString(applicant?.country),
    dateOfBirth: toDisplayDate(applicant?.birth_date),
    passportNo: pickString(applicant?.passport_number),
    passportIssued: toDisplayDate(applicant?.passport_issued),
    validPassport: toDisplayDate(applicant?.valid_passport),
    phone: pickString(applicant?.phone),
    email: pickString(applicant?.email),
    managersEmail: pickString(item.managers_email),
    agentsEmail: pickString(item.agents_email),
    course,
    levelEducation: degree,
    fieldOfStudy: pickString(item.field_of_study, task?.category),
    formOfEducation: pickString(item.form_of_education, task?.form_of_education),
    duration: pickString(item.duration, task?.duration),
    mediumOfInstruction: pickString(item.medium_of_instruction, task?.medium_of_instruction),
    admissionSemester: pickString(item.admission_semester, task?.admission_semester),
    intake: pickString(item.intake, task?.intake),
    currentSemester: pickString(item.current_semester),
    generalStatus: resolveOfficeAccountingGeneralStatusLabel(pickString(item.general_status)),
    admissionNo,
    dataAdmission: toDisplayDate(item.data_admission),
    tuitionFee: pickString(item.tuition_fee, task?.tuition_fee),
    certificationEqualization: pickString(applicant?.certification_equalization),
    statusAdmissionPayment: pickString(item.status_admission_payment),
    groupNo: pickString(item.group_no),
    residencePermit: pickString(task?.residence_permit),
    validUntil: toDisplayDate(item.valid_until),
    orderOfThePD: pickString(item.order_of_the_pd),
    opDData: toDisplayDate(item.op_d_data),
    expulsionOfThePd: pickString(item.expulsion_of_the_pd),
    epDData: toDisplayDate(item.ep_d_data),
    orderOfTheMc: pickString(item.order_of_the_mc),
    omcData: toDisplayDate(item.omc_data),
    expulsionOfTheMc: pickString(item.expulsion_of_the_mc),
    emcData: toDisplayDate(item.emc_data),
    orderForAcademicLeave: pickString(item.order_for_academic_leave),
    durationAl: pickString(item.duration_al),
    statusAl: pickString(item.status_al),
    emailStudentInBase: pickString(item.email_student_in_base),
    statusEmail: pickString(item.status_email),
    dateAddEdbo: toDisplayDate(item.date_add_edbo),
    statusEdbo: pickString(item.status_edbo),
    invitationFee:
      pickString(item.invitation_fee) || resolveInvitationFeeFromFormOfEducation(task?.form_of_education) || "",
    ifDate: toDisplayDate(item.if_date),
    invitationFeePaid:
      formatOfficeAccountingIncomeRecordsTotal(invitationFeePaidRecords) || pickString(item.invitation_fee_paid),
    ifDatePaid: getLastOfficeAccountingIncomeRecordDate(invitationFeePaidRecords),
    statusInvitationFee: pickString(item.status_invitation_fee),
    invoiceNumber: pickString(item.invoice_number),
    dataInvoice: toDisplayDate(item.data_invoice),
    invoiceAmount: pickString(item.invoice_amount),
    paymentFactUsd:
      formatOfficeAccountingIncomeRecordsTotal(paymentFactUsdRecords) || pickString(item.payment_fact_usd),
    creditDateUsd: getLastOfficeAccountingIncomeRecordDate(paymentFactUsdRecords),
    totalAmountUsd: pickString(item.total_amount_usd),
    totalPaidUsd: pickString(item.total_paid_usd),
    debtBalanceUsd: pickString(item.debt_balance_usd),
    balanceStatusUsd: pickString(item.balance_status_usd),
    paymentFactEur: pickString(item.payment_fact_eur),
    creditDateEur: toDisplayDate(item.credit_date_eur),
    totalMissedClassesEur: pickString(item.total_missed_classes_eur),
    balanceMissedClassesEur: pickString(item.balance_missed_classes_eur),
    statusMissedClasses: pickString(item.status_missed_classes),
    totalNostrificationsEur: pickString(item.total_nostrifications_eur),
    balanceNostrificationsEur: pickString(item.balance_nostrifications_eur),
    statusNostrification: pickString(item.status_nostrification),
    preparatoryDepartment: pickString(applicant?.preparatory_department, item.preparatory_department),
    costFirstSemester: pickString(item.cost_first_semester),
    costSecondSemester: pickString(item.cost_second_semester),
    costThirdSemester: pickString(item.cost_third_semester),
    costFouthSemester: pickString(item.cost_fourth_semester),
    costFifthSemester: pickString(item.cost_fifth_semester),
    costSixthSemester: pickString(item.cost_sixth_semester),
    costSeventhSemester: pickString(item.cost_seventh_semester),
    costEighthSemester: pickString(item.cost_eighth_semester),
    costNinthSemester: pickString(item.cost_ninth_semester),
    costTenthSemester: pickString(item.cost_tenth_semester),
    costEleventhSemester: pickString(item.cost_eleventh_semester),
    costTwelfthSemester: pickString(item.cost_twelfth_semester),
    pgPhdCostFirstSemester: pickString(item.pg_phd_cost_first_semester),
    pgPhdCostSecondSemester: pickString(item.pg_phd_cost_second_semester),
    pgPhdCostThirdSemester: pickString(item.pg_phd_cost_third_semester),
    pgPhdCostFourthSemester: pickString(item.pg_phd_cost_fourth_semester),
    pgPhdCostFifthSemester: pickString(item.pg_phd_cost_fifth_semester),
    pgPhdCostSixthSemester: pickString(item.pg_phd_cost_sixth_semester),
    otherExpenses: pickString(task?.other_expenses),
    transfer: pickString(item.transfer, task?.transfer_fee),
    insurance: pickString(item.insurance, task?.medical_insurance),
    admissionFee: pickString(task?.admission_fee),
    temporaryResidencePermission: pickString(item.temporary_residence_permission),
    hostelAccommodation: pickString(item.hostel_accommodation, task?.acommodation),
    paymentForMissedClasses: pickString(item.payment_for_missed_classes),
    nostrification: pickString(item.nostrification),
    whoose: pickString(task?.whoose),
  };
};

export const normalizeApplicantAccountingListResponse = (
  payload: unknown,
): { data: IOfficeAccountingTableRow[]; count_form: number; filters_list: IFiltersList } => {
  if (!payload) return { data: [], count_form: 0, filters_list: {} as IFiltersList };

  const root = payload as { data?: IApplicantAccountingListItem[]; count?: number };
  const rows = Array.isArray(root.data) ? root.data : [];
  const countRaw = root.count ?? rows.length;
  const count_form = Number.isFinite(Number(countRaw)) ? Number(countRaw) : rows.length;

  return {
    data: rows.map(mapApplicantAccountingItemToTableRow),
    count_form,
    filters_list: {} as IFiltersList,
  };
};

export const normalizeOfficeAccountingResponse = (
  payload: unknown,
): { data: IOfficeAccountingTableRow[]; count_form: number; filters_list: IFiltersList } => {
  if (!payload) return { data: [], count_form: 0, filters_list: {} as IFiltersList };

  const root = payload as Record<string, unknown>;
  const rows = Array.isArray(root.data) ? root.data : Array.isArray(payload) ? (payload as unknown[]) : [];
  const countRaw = root.count ?? root.count_applicant ?? root.count_form ?? rows.length;
  const count_form = Number.isFinite(Number(countRaw)) ? Number(countRaw) : rows.length;
  const filters_list = (root.filters_list ?? {}) as IFiltersList;

  const data = rows.map((row, index) => {
    if (isGroupedRow(row)) {
      const applicantInfo = pickApplicantInfo(row);
      const userInfo = pickUserInfo(row);
      const studentInfoId =
        parseStudentId(applicantInfo?.id) ||
        parseStudentId(row.student_id) ||
        parseStudentId(row.userInfo?.student_id) ||
        parseStudentId(row.userInfo?.id) ||
        index + 1;
      return mapApplicantDetailToTableRow(studentInfoId, userInfo, applicantInfo);
    }
    const flat = row as Record<string, unknown> & IApplicantInfoDetail;
    const studentInfoId = parseStudentId(flat.id ?? flat.student_id ?? flat.studentId) || index + 1;
    return mapApplicantDetailToTableRow(studentInfoId, undefined, flat, flat);
  });

  return { data, count_form, filters_list };
};

/** Table column id → applicant-info API field for PUT. */
export const mapColumnIdToApplicantField = (columnId: string): keyof IApplicantInfoPayload | null => {
  const readonlyColumns = new Set([
    "officeAccountingId",
    "applicantId",
    "fullName",
    "comments",
    "campus",
    "country",
    "dateOfBirth",
    "passportNo",
    "passportIssued",
    "validPassport",
    "phone",
    "email",
  ]);
  if (readonlyColumns.has(columnId)) return null;
  return columnId as keyof IApplicantInfoPayload;
};

export const getOfficeAccountingRowId = (row: IOfficeAccountingTableRow): number => row.student_info_id;
